import React from 'react';
import Relay from 'react-relay';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl, FormattedMessage, FormattedDate, FormattedRelative, IntlShape } from 'react-intl';
import { Link } from 'react-router';
import { fromGlobalId } from 'graphql-relay';
import AppPanel from '../fw/AppPanel.react';
import urlMapping from '../app/URLMapping';
import types from '../../common/model-api/types';
import { TEST_STATUS_PASSED, TEST_STATUS_FAILED, TEST_STATUS_PENDING } from '../../common/types/TestTypes';
import RejectTestRunMutation, { hasReviewVersion, rejectTestRunMessages } from './RejectTestRunMutation';
import { notification } from './notification';
import { getReviewMutationOutcome } from './mutationPayload';

const uuidRegex = /[0-9a-fA-F\-]{30,40}/;

const messages = defineMessages({
    rejectRunFailed: {
        id: 'snapshotTesting.rejectRunFailed',
        defaultMessage: 'Failed to reject test run. Please try again.',
    },
});

function normalizeSnapshotEntityId(id?: string | null): string | null {
    if (!id) {
        return null;
    }

    if (uuidRegex.test(id)) {
        return id;
    }

    try {
        const globalId = fromGlobalId(id);
        return globalId.id || id;
    } catch (e) {
        return id;
    }
}

function getPreferredSnapshotEntityId(entity?: { id?: string | null; realId?: string | null } | null): string | null {
    if (!entity) {
        return null;
    }

    return entity.realId || normalizeSnapshotEntityId(entity.id);
}

function normalizeSnapshotDate(dateVal?: string | Date | number | null): Date | undefined {
    if (!dateVal) {
        return undefined;
    }

    let normalizedDate = new Date(dateVal as any);

    if (isNaN(normalizedDate.getTime()) && (typeof dateVal === 'string' || typeof dateVal === 'number') && !isNaN(Number(dateVal))) {
        normalizedDate = new Date(Number(dateVal));
    }

    return isNaN(normalizedDate.getTime()) ? undefined : normalizedDate;
}

interface TestInvocationRunsViewProps {
    intl: IntlShape;
    testInvocation: any;
    params: {
        flowId: string;
        organisationId: string;
        id: string;
    };
    relay: any;
}

export class TestInvocationRunsView extends React.Component<TestInvocationRunsViewProps> {
    static propTypes = {
        intl: PropTypes.object.isRequired,
        params: PropTypes.object.isRequired,
        testInvocation: PropTypes.object.isRequired,
    };

    static contextTypes = {
        router: PropTypes.object,
    };

    refreshData = () => {
        this.props.relay.forceFetch();
    };

    refreshViaCanonicalRoute = () => {
        const { testInvocation } = this.props;
        const invocationId = getPreferredSnapshotEntityId(testInvocation);

        if (!invocationId || !this.context || !this.context.router || !this.context.router.replace) {
            this.refreshData();
            return;
        }

        this.context.router.replace(urlMapping.getUrl('snapshotTesting', 'runs', {
            organisation_id: testInvocation.organisation_id,
            flow_id: testInvocation.flow_id,
            id: invocationId,
        }));
        // Route replacement only normalizes the URL; refetch so a warm cache cannot keep stale runs.
        this.refreshData();
    };

    handleRejectRun = (run: any) => {
        const { testInvocation } = this.props;
        if (!hasReviewVersion(testInvocation)) {
            notification.error(this.props.intl.formatMessage(rejectTestRunMessages.reviewVersionUnavailable));
            return;
        }

        Relay.Store.commitUpdate(
            new RejectTestRunMutation({
                testInvocation: {
                    id: testInvocation.id,
                    realId: getPreferredSnapshotEntityId(testInvocation) || undefined,
                    version: testInvocation.version,
                },
                testRun: {
                    id: run.id,
                    realId: getPreferredSnapshotEntityId(run) || undefined,
                },
            }),
            {
                onSuccess: (response: any) => {
                    const rejectFailed = this.props.intl.formatMessage(messages.rejectRunFailed);
                    const outcome = getReviewMutationOutcome(response && response.rejectTestRun, rejectFailed, rejectFailed);
                    if (!outcome.ok) {
                        // A refused reject (conflict, wrong state, permission) did not change anything:
                        // keep the error visible and do not refresh as if it had succeeded.
                        notification.error(outcome.error);
                        return;
                    }
                    this.refreshViaCanonicalRoute();
                },
                onFailure: (error: any) => {
                    console.error('Failed to reject test run', error);
                    notification.error(this.props.intl.formatMessage(messages.rejectRunFailed));
                },
            }
        );
    };

    renderStatus(status: string | undefined) {
        let labelClass = 'status-default';
        let statusText = status || 'Not run';

        if (status === TEST_STATUS_PASSED) {
            labelClass = 'status-passed';
        } else if (status === TEST_STATUS_FAILED) {
            labelClass = 'status-failed';
        } else if (status === TEST_STATUS_PENDING) {
            labelClass = 'status-pending';
        }

        return <span className={`status-label ${labelClass}`}>{statusText}</span>;
    }

    render() {
        const { flowId, organisationId } = this.props.params;
        const { testInvocation } = this.props;

        if (!testInvocation) {
            return <div><FormattedMessage defaultMessage="Loading..." id="common.loading" /></div>;
        }

        const runs = testInvocation.runs ? testInvocation.runs.edges.map((e: any) => e.node) : [];
        const invocationId = getPreferredSnapshotEntityId(testInvocation);

        const breadcrumbParams = [
            { type: types['organisations'], data: { realId: organisationId, id: organisationId, name: 'Organisation' } },
            { type: types['flows'], data: { realId: flowId, id: flowId, name: 'Flow' } },
            { type: { entity: () => 'snapshotTesting', getDefaultTitle: () => 'Test Results' }, data: { name: 'Test Results' } }
        ];

        return (
            <AppPanel
                breadcrumbParams={breadcrumbParams}
                title={this.props.intl.formatMessage({ id: 'snapshotTesting.runsTitle', defaultMessage: 'Test Runs' })}
            >
                <div className="flex-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
                    <h3>
                        <i className="fa fa-history" /> <FormattedMessage defaultMessage="Runs for Test: {name}" id="snapshotTesting.runsForTest" values={{ name: testInvocation.name }} />
                    </h3>
                    <button className="btn btn-default" onClick={this.refreshData}>
                        <i className="fa fa-refresh" /> <FormattedMessage defaultMessage="Refresh" id="common.refresh" />
                    </button>
                </div>

                <div className="test-results-table">
                    <div className="test-results-table__scroll">
                    <table className="table table-striped table-hover">
                        <thead>
                            <tr>
                                <th><FormattedMessage defaultMessage="Date" id="snapshotTesting.column.date" /></th>
                                <th><FormattedMessage defaultMessage="Status" id="snapshotTesting.column.status" /></th>
                                <th><FormattedMessage defaultMessage="Duration" id="snapshotTesting.column.duration" /></th>
                                <th><FormattedMessage defaultMessage="Error" id="snapshotTesting.column.error" /></th>
                                <th className="actions-column"><FormattedMessage defaultMessage="Actions" id="snapshotTesting.column.actions" /></th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.length === 0 ? (
                                <tr>
                                    <td className="text-center" colSpan={5}>
                                        <FormattedMessage defaultMessage="No runs available." id="snapshotTesting.noRuns" />
                                    </td>
                                </tr>
                            ) : runs.map((run: any) => (
                                <tr key={getPreferredSnapshotEntityId(run) || run.id}>
                                    <td>
                                        {normalizeSnapshotDate(run.createdAt) ? (
                                            <div>
                                                <FormattedDate
                                                    day="numeric"
                                                    hour="numeric"
                                                    minute="numeric"
                                                    month="short"
                                                    value={normalizeSnapshotDate(run.createdAt)}
                                                    year="numeric"
                                                />
                                                <div className="text-muted small"><FormattedRelative value={normalizeSnapshotDate(run.createdAt)} /></div>
                                            </div>
                                        ) : '-'}
                                    </td>
                                    <td>{this.renderStatus(run.status)}</td>
                                    <td>{run.duration ? `${run.duration} ms` : '-'}</td>
                                    <td className="text-danger">{run.error || '-'}</td>
                                    <td className="actions-column">
                                        <div className="btn-group btn-group-xs">
                                            <Link
                                                className="btn btn-default btn-xs"
                                                to={urlMapping.getUrl('snapshotTesting', 'validateRun', {
                                                    organisation_id: testInvocation.organisation_id,
                                                    flow_id: testInvocation.flow_id,
                                                    id: invocationId,
                                                    runId: getPreferredSnapshotEntityId(run),
                                                })}
                                            >
                                                <FormattedMessage defaultMessage="View Diff" id="snapshotTesting.viewDiff" />
                                            </Link>
                                            <button
                                                className="btn btn-danger btn-xs"
                                                onClick={() => this.handleRejectRun(run)}
                                            >
                                                <FormattedMessage defaultMessage="Reject" id="snapshotTesting.reject" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            </AppPanel>
        );
    }
}

export default Relay.createContainer(injectIntl(TestInvocationRunsView), {
    initialVariables: {
        id: null,
    },
    fragments: {
        testInvocation: () => Relay.QL`
            fragment on TestInvocation {
                id
                realId
                version
                name
                organisation_id
                flow_id
                runs(first: 100) {
                    edges {
                        node {
                            id
                            realId
                            status
                            duration
                            createdAt
                            error
                        }
                    }
                }
            }
        `,
    },
});
